const formatUtils = () => {
  /**
   * Format the date in the format "DD-MM-YYYY"
   * @param time
   * @param showMonth
   * @param showTime
   */
  const formatDate = (time: string, showMonth: boolean = false, showTime: boolean = false) => {
    const date = new Date(time);

    // Define the locale
    const locale = 'nl-NL'; // Dutch locale

    // Format date
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      year: 'numeric',
      month: showMonth ? 'short' : '2-digit', // "jan" or "01"
      timeZone: 'Europe/Amsterdam', // ✅ Forces UTC formatting on both client and server
    };
    const formattedDate = new Intl.DateTimeFormat(locale, dateOptions).format(date);

    // Format time (if needed)
    let formattedTime = '';
    if (showTime) {
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // 24-hour format
        timeZone: 'Europe/Amsterdam', // ✅ Forces UTC formatting on both client and server
      };
      formattedTime = new Intl.DateTimeFormat(locale, timeOptions).format(date);
    }

    // Return the combined result
    return showTime ? `${formattedDate}, ${formattedTime}` : formattedDate;
  };

  /**
   * Format the date in the format "dd <monthName>"
   * @param time
   */
  const formatDateWithFullMonth = (time: string) => {
    const date = new Date(time);

    // Available month names
    const monthNames = [
      'januari',
      'februari',
      'maart',
      'april',
      'mei',
      'juni',
      'juli',
      'augustus',
      'september',
      'oktober',
      'november',
      'december',
    ];

    // Get the individual components in UTC
    const day = date.getUTCDate().toString().padStart(2, '0');
    const monthName = monthNames[date.getUTCMonth()];

    return `${day} ${monthName}`;
  };

  function toCamelCase(str: string): string {
    return str.replace(/([-_][a-z])/gi, (c) => c.toUpperCase().replace(/[-_]/g, ''));
  }

  /**
   * Convert the keys of an object/json/array of objects from snake-case to camelCase
   * This also has a routine to convert values to camelCase, right now it's only used to for 'collection',
   * so collection: content_embed > collection: contentEmbed
   * @param obj
   */
  function objectKeysToCamelCase<T extends object>(obj: T): T {
    /**
     * string, number, null, undefined, boolean, ...
     */
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    /**
     * Array
     */
    if (Array.isArray(obj)) {
      return obj.map((item) => objectKeysToCamelCase(item)) as T;
    }

    /**
     * JSON
     */
    const output: Record<string, unknown> = {};

    Object.entries(obj).forEach(([key, value]) => {
      const newKey = key.includes('_') ? toCamelCase(key) : key;
      output[newKey] = objectKeysToCamelCase(value as Record<string, unknown>);
    });

    return output as T;
  }

  function splitFileName(path: string) {
    // Find the position of the last "/"
    const lastSlashIndex = path.lastIndexOf('/');

    // Find the position of the last "."
    const lastDotIndex = path.lastIndexOf('.');

    // Check if the dot is after the last "/"
    if (lastDotIndex > lastSlashIndex) {
      // Extract the desired part between the last "/" and "."
      return {
        base: path.substring(0, lastSlashIndex),
        name: path.substring(lastSlashIndex + 1, lastDotIndex),
        extension: path.substring(lastDotIndex),
      };
    }

    return {
      base: path.substring(0, lastSlashIndex),
      name: path.substring(lastSlashIndex + 1),
      extension: '',
    };
  }

  function isNumber(value: string | number): boolean {
    // If the value is a number, return true
    if (typeof value === 'number') {
      return true;
    }
    // Use a regular expression to check if the string contains only digits
    return /^-?\d+(\.\d+)?$/.test(value);
  }

  function toText(blocks?: never) {
    if (!blocks) {
      return '';
    }

    return blocks
      .map((block: never) => {
        let text = '';
        switch (block.type) {
          case 'header':
          case 'paragraph':
            text = block.data.text;
            break;
          case 'nestedlist':
            text = processNestedList(block);
            break;
          default:
            break;
        }
        return stripHTML(text);
      })
      .join(' ');
  }

  function processNestedList(block: never) {
    const stack = block.data.items;
    const texts = [];

    while (stack.length) {
      const next = stack.shift();
      texts.push(next.content);
      if (next.items) {
        stack.unshift(...next.items);
      }
    }
    return texts.join(' ');
  }

  function stripHTML(s: string) {
    return s?.replace(/<\/?[^>]+(>|$)/g, '') || '';
  }

  function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function splitType(type: string) {
    // if type contains a /, split it into prefix and chapter2. Else, return the type as prefix
    if (!type.includes('/')) {
      return {};
    }
    const parts = type.split('/');
    return {
      chapter1: parts[0] || 'artikelen',
      chapter2: parts[1],
    };
  }

  function slugify(input: string): string {
    // Remove leading and trailing whitespaces, convert to lowercase and replace whitespaces with a dash
    return input.trim().toLowerCase().replace(/\s+/g, '-');
  }

  function formatIsoDurationNoZeros(durationString?: string): string | undefined {
    if (!durationString) return;

    const timePart = durationString.split('T')[1] || '';

    const h = timePart.match(/(\d+)H/);
    const m = timePart.match(/(\d+)M/);
    const s = timePart.match(/(\d+)S/);

    const parts = [h ? parseInt(h[1]) : 0, m ? parseInt(m[1]) : 0, s ? parseInt(s[1]) : 0];

    // Find the index of the first non-zero value (e.g., skip hours if 0)
    const firstNonZeroIndex = parts.findIndex((val) => val > 0);

    // If the whole string is empty (PT0S), return "00:00" or similar
    if (firstNonZeroIndex === -1) return '00:00';

    return (
      parts
        // Slice from the first non-zero unit to the end
        .slice(firstNonZeroIndex)
        // Ensure we always show at least two parts (e.g., "05:00" instead of just "05")
        // Remove the slice adjustment if you want "5 seconds" to just be "05"
        .map((val) => val.toString().padStart(2, '0'))
        .join(':')
    );
  }

  function decodeHtmlEntities(str: string): string {
    if (!str) return '';

    // 1. Decode decimal numeric entities (e.g., &#128512;)
    let decoded = str.replace(/&#([0-9]+);/g, (_, dec) => {
      return String.fromCodePoint(Number(dec));
    });

    // 2. Decode hexadecimal numeric entities (e.g., &#x1F600;)
    decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
      return String.fromCodePoint(parseInt(hex, 16));
    });

    // 3. Map common named entities to protect HTML layout
    const namedEntities: Record<string, string> = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
    };

    return decoded.replace(/&[a-zA-Z0-9#]+;/g, (match) => namedEntities[match] || match);
  }

  return {
    slugify,
    formatDate,
    formatDateWithFullMonth,
    formatIsoDurationNoZeros,
    toCamelCase,
    objectKeysToCamelCase,
    splitFileName,
    capitalizeFirstLetter,
    isNumber,
    toText,
    splitType,
    stripHTML,
    decodeHtmlEntities,
  };
};

export default formatUtils;
